import { Box } from "@mui/material"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import { type FC, useCallback, useMemo } from "react"

import { formatAssociatedTestTemplateLimits } from "../../../TestTemplates/utils"

import ProductTestResultRowActions from "./ProductTestResultRowActions"
import { ProductTestResultStatusIndicator } from "./ProductTestResultStatusIndicator"

import { ColoredLabel } from "@/components/ColoredLabel"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { PermissionActionType } from "@/constants/permissions.ts"
import type { AssociatedTestTemplate, ProductTest, ProductTestResult } from "@/graphql/codegen/graphql"
import { ProductTestResultStatus, TestTemplateComparison } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { getTestTemplateComparisonLabel } from "@/screens/ManageProducts/components/TestTemplates/utils"
import { isTestResultsValid } from "@/screens/Products/components/Product/Quality/TestResults/utils"
import colors from "@/styles/colors"

interface Props {
  productTest: ProductTest
  onOverride: (productTestResult: ProductTestResult) => void
  onApprovalRequest: (productTestResult: ProductTestResult) => void
  onApprovalCancel: (productTestResult: ProductTestResult) => void
}

export const ProductTestResultsTable: FC<Props> = ({
  productTest,
  onOverride,
  onApprovalRequest,
  onApprovalCancel,
}) => {
  const { hasPermissions } = usePermissionManager()
  const canOverrideResult = hasPermissions([PermissionActionType.TEST_RESULT_OVERRIDE])
  const canRequestApproval = hasPermissions([PermissionActionType.DASHBOARD_MY_REQUESTS])
  const canMaintain = hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE])
  const canOverride = useCallback(
    (productTestResult: ProductTestResult) => {
      if (!canOverrideResult) {
        return false
      }

      if (productTestResult.status === ProductTestResultStatus.ResultsNotWithinSpec) {
        const isBooleanComparison = [TestTemplateComparison.PassFail, TestTemplateComparison.YesNo].includes(
          productTestResult.associatedTestTemplate?.testTemplate?.comparison as TestTemplateComparison
        )
        const isTestResultValid = isTestResultsValid(
          productTestResult.associatedTestTemplate ?? ({} as AssociatedTestTemplate),
          Number(productTestResult.value)
        )
        return isBooleanComparison || !isTestResultValid
      }
    },
    [canOverrideResult]
  )
  const canManageApproval = useCallback(
    (productTestResult: ProductTestResult) =>
      (canRequestApproval || canMaintain) && productTestResult.status === ProductTestResultStatus.ResultsNotWithinSpec,
    [canRequestApproval, canMaintain]
  )

  const columns = useMemo<ColumnDef<ProductTestResult>[]>(() => {
    return [
      {
        accessorKey: "sequenceNumber",
        header: "Seq.",
        size: 25,
        cell: ({ row }) => row.original.associatedTestTemplate?.sequenceNumber,
      },
      {
        accessorKey: "testNumber",
        header: "Test #",
        size: 65,
        cell: ({ row }) => row.original.associatedTestTemplate?.testTemplate?.externalSystemId,
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 65,
        cell: ({ row }) => row.original.associatedTestTemplate?.testTemplate?.description,
      },
      {
        accessorKey: "comparison",
        header: "Comparison",
        size: 65,
        cell: ({ row }) =>
          getTestTemplateComparisonLabel(row.original.associatedTestTemplate?.testTemplate?.comparison ?? ""),
      },
      {
        accessorKey: "limits",
        header: "Limit(s)",
        size: 65,
        cell: ({ row }) =>
          formatAssociatedTestTemplateLimits(row.original.associatedTestTemplate ?? ({} as AssociatedTestTemplate)),
      },
      {
        accessorKey: "resultsUnitOfMeasure",
        header: "Results UoM",
        size: 65,
        cell: ({ row }) => <ColoredLabel label={row.original.value || "-"} backgroundColor={colors.primary["100"]} />,
      },
      {
        accessorKey: "status",
        header: "Result",
        size: 25,
        cell: ({ row }) => <ProductTestResultStatusIndicator status={row.original.status} />,
      },
      ...(!productTest.isHistorical && (canMaintain || canRequestApproval)
        ? [
            {
              accessorKey: "actions",
              enableSorting: false,
              header: "",
              cell: ({ row }: CellContext<ProductTestResult, unknown>) =>
                (canOverride(row.original) || canManageApproval(row.original)) && !productTest.isHistorical ? (
                  <Box className='flex items-center justify-end'>
                    <ProductTestResultRowActions
                      row={row}
                      canOverride={canOverride(row.original)}
                      canManageApproval={canManageApproval(row.original)}
                      onOverride={onOverride}
                      onApprovalRequest={onApprovalRequest}
                      onApprovalCancel={onApprovalCancel}
                    />
                  </Box>
                ) : null,
              size: 35,
            },
          ]
        : []),
    ]
  }, [
    onOverride,
    onApprovalRequest,
    onApprovalCancel,
    productTest,
    canMaintain,
    canRequestApproval,
    canOverride,
    canManageApproval,
  ])

  return (
    <AerosBaseTable<ProductTestResult>
      slotProps={{
        headerCell: { className: productTest.isHistorical ? "bg-tertiary-yellow-100" : "bg-primary-100" },
      }}
      features={{
        tanstackOptions: {
          data: productTest.results ?? [],
          columns,
          enableSorting: false,
          getCoreRowModel: getCoreRowModel(),
        },
      }}
      noDataMessage='No test results found'
    />
  )
}
